import { useState, useEffect, useCallback } from 'react'
import { X, Plus, Trash2, CheckCircle, AlertCircle, Loader2, Users, ChevronDown, ChevronUp } from 'lucide-react'
import { minoriApi, pepApi } from '../../services/api'
import { useNotification } from '../../contexts/NotificationContext'

/**
 * PepImportRapido — Modal per creare PEP base per più minori
 *
 * 1. Carica minori attivi + dashboard PEP per sapere chi ha già un PEP
 * 2. Lista minori con checkbox di selezione
 * 3. Per ogni selezionato: ente inviante, data avvio, obiettivi inline
 * 4. Crea N PEP in batch via POST /api/pep/import-rapido
 *
 * Props:
 *   onClose   - callback chiusura
 *   onDone    - callback dopo creazione (per reload dati)
 */

const ENTE_OPTIONS = [
  { value: 'servizi_sociali', label: 'Servizi Sociali' },
  { value: 'tribunale_minori', label: 'Tribunale Minori' },
  { value: 'scuola', label: 'Scuola' },
  { value: 'famiglia', label: 'Famiglia' },
  { value: 'asl', label: 'ASL' },
  { value: 'comunita', label: 'Comunità' },
  { value: 'altro', label: 'Altro' }
]

const AREA_OPTIONS = [
  { value: 'autonomia', label: 'Autonomia' },
  { value: 'socializzazione', label: 'Socializzazione' },
  { value: 'apprendimento', label: 'Apprendimento' },
  { value: 'comportamento', label: 'Comportamento' },
  { value: 'gestione_emotiva', label: 'Gestione emotiva' },
  { value: 'rispetto_regole', label: 'Rispetto regole' },
  { value: 'motivazione', label: 'Motivazione' },
  { value: 'cura_di_se', label: 'Cura di sé' },
  { value: 'relazioni_familiari', label: 'Relazioni familiari' },
  { value: 'altro', label: 'Altro' }
]

const PRIORITA_OPTIONS = [
  { value: 'alta', label: 'Alta', color: 'text-red-600 bg-red-50 border-red-200' },
  { value: 'media', label: 'Media', color: 'text-orange-600 bg-orange-50 border-orange-200' },
  { value: 'bassa', label: 'Bassa', color: 'text-green-600 bg-green-50 border-green-200' }
]

function emptyObiettivo() {
  return { area: 'comportamento', titolo: '', priorita: 'media' }
}

function emptyConfig() {
  return {
    enteInviante: '',
    dataAvvio: new Date().toISOString().slice(0, 10),
    obiettivi: [emptyObiettivo()],
    expanded: true
  }
}

export default function PepImportRapido({ onClose, onDone }) {
  const { showSuccess, showError } = useNotification()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [minori, setMinori] = useState([])
  const [pepAttiviIds, setPepAttiviIds] = useState(new Set())
  const [selected, setSelected] = useState(new Set())
  const [configs, setConfigs] = useState({}) // minoreId -> config

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [minoriRes, dashRes] = await Promise.allSettled([
        minoriApi.list({ attivo: true }),
        pepApi.getDashboard()
      ])

      // Estrai minori
      let allMinori = []
      if (minoriRes.status === 'fulfilled') {
        const d = minoriRes.value?.data
        allMinori = Array.isArray(d) ? d
          : Array.isArray(d?.data) ? d.data
          : Array.isArray(d?.minori) ? d.minori
          : Array.isArray(d?.data?.minori) ? d.data.minori
          : []
      }
      setMinori(allMinori.sort((a, b) => (a.nomeCompleto || '').localeCompare(b.nomeCompleto || '')))

      // Estrai IDs con PEP attivo
      if (dashRes.status === 'fulfilled') {
        const pepAttivi = dashRes.value?.data?.data?.pepAttivi || []
        const ids = new Set(pepAttivi.map(p => {
          const mid = p.minoreId?._id || p.minoreId
          return typeof mid === 'object' ? mid.toString() : mid
        }))
        setPepAttiviIds(ids)
      }
    } catch {
      showError('Errore nel caricamento dei dati')
    } finally {
      setLoading(false)
    }
  }

  const toggleSelect = useCallback((minoreId) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(minoreId)) {
        next.delete(minoreId)
      } else {
        next.add(minoreId)
        // Inizializza config se non esiste
        setConfigs(c => c[minoreId] ? c : { ...c, [minoreId]: emptyConfig() })
      }
      return next
    })
  }, [])

  const updateConfig = useCallback((minoreId, field, value) => {
    setConfigs(prev => ({
      ...prev,
      [minoreId]: { ...prev[minoreId], [field]: value }
    }))
  }, [])

  const updateObiettivo = useCallback((minoreId, obIdx, field, value) => {
    setConfigs(prev => {
      const config = { ...prev[minoreId] }
      const obiettivi = [...config.obiettivi]
      obiettivi[obIdx] = { ...obiettivi[obIdx], [field]: value }
      return { ...prev, [minoreId]: { ...config, obiettivi } }
    })
  }, [])

  const addObiettivo = useCallback((minoreId) => {
    setConfigs(prev => {
      const config = { ...prev[minoreId] }
      return { ...prev, [minoreId]: { ...config, obiettivi: [...config.obiettivi, emptyObiettivo()] } }
    })
  }, [])

  const removeObiettivo = useCallback((minoreId, obIdx) => {
    setConfigs(prev => {
      const config = { ...prev[minoreId] }
      const obiettivi = config.obiettivi.filter((_, i) => i !== obIdx)
      return { ...prev, [minoreId]: { ...config, obiettivi: obiettivi.length ? obiettivi : [emptyObiettivo()] } }
    })
  }, [])

  const toggleExpanded = useCallback((minoreId) => {
    setConfigs(prev => ({
      ...prev,
      [minoreId]: { ...prev[minoreId], expanded: !prev[minoreId]?.expanded }
    }))
  }, [])

  // Conteggi
  const minoriDisponibili = minori.filter(m => !pepAttiviIds.has(m._id))
  const selectedCount = selected.size
  const pepEsistenti = minori.length - minoriDisponibili.length
  const saltati = minoriDisponibili.length - selectedCount

  // Validazione
  const isValid = () => {
    if (selectedCount === 0) return false
    for (const mid of selected) {
      const config = configs[mid]
      if (!config?.dataAvvio) return false
      for (const ob of config.obiettivi) {
        if (!ob.titolo.trim()) return false
      }
    }
    return true
  }

  const handleSubmit = async () => {
    if (!isValid()) return
    setSubmitting(true)
    try {
      const payload = Array.from(selected).map(mid => {
        const config = configs[mid]
        return {
          minoreId: mid,
          dataAvvio: config.dataAvvio,
          contestoIniziale: config.enteInviante ? {
            enteInviante: { tipo: config.enteInviante }
          } : undefined,
          obiettivi: config.obiettivi
            .filter(ob => ob.titolo.trim())
            .map(ob => ({
              area: ob.area,
              titolo: ob.titolo.trim(),
              priorita: ob.priorita
            }))
        }
      })

      const res = await pepApi.importRapido(payload)
      const data = res.data?.data
      const createdCount = data?.created?.length || 0
      const errorCount = data?.errors?.length || 0

      if (errorCount > 0 && createdCount > 0) {
        showSuccess(`Creati ${createdCount} PEP. ${errorCount} non creati (duplicati o errori).`)
      } else if (errorCount > 0) {
        showError(`Nessun PEP creato. ${errorCount} errori.`)
      } else {
        showSuccess(`${createdCount} PEP creati con successo`)
      }

      onDone?.()
      onClose()
    } catch (error) {
      const msg = error.response?.data?.message || 'Errore nella creazione dei PEP'
      showError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  // Seleziona/deseleziona tutti i disponibili
  const toggleAll = () => {
    if (selectedCount === minoriDisponibili.length) {
      setSelected(new Set())
    } else {
      const allIds = new Set(minoriDisponibili.map(m => m._id))
      setSelected(allIds)
      // Inizializza config per tutti
      setConfigs(prev => {
        const next = { ...prev }
        for (const m of minoriDisponibili) {
          if (!next[m._id]) next[m._id] = emptyConfig()
        }
        return next
      })
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 w-full max-w-3xl my-4 mx-4 rounded-xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Users className="h-5 w-5 text-primary-500" />
              Import rapido PEP
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Crea un progetto educativo base per più minori
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
            </div>
          ) : minori.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Nessun minore attivo nel centro
            </div>
          ) : (
            <div className="space-y-3">
              {/* Seleziona tutti */}
              <div className="flex items-center justify-between pb-2 border-b border-gray-100 dark:border-gray-700">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedCount > 0 && selectedCount === minoriDisponibili.length}
                    onChange={toggleAll}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Seleziona tutti i disponibili ({minoriDisponibili.length})
                </label>
                <span className="text-xs text-gray-400">
                  {pepEsistenti > 0 && `${pepEsistenti} con PEP già attivo`}
                </span>
              </div>

              {/* Lista minori */}
              {minori.map(m => {
                const hasPep = pepAttiviIds.has(m._id)
                const isSelected = selected.has(m._id)
                const config = configs[m._id]
                const age = m.dataNascita ? calcAge(m.dataNascita) : null

                return (
                  <div
                    key={m._id}
                    className={`border rounded-lg transition-colors ${
                      hasPep
                        ? 'border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 opacity-60'
                        : isSelected
                          ? 'border-primary-300 dark:border-primary-600 bg-primary-50/50 dark:bg-primary-900/10'
                          : 'border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    {/* Row principale */}
                    <div className="flex items-center gap-3 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={hasPep}
                        onChange={() => toggleSelect(m._id)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-40"
                      />

                      {/* Avatar */}
                      <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-semibold text-primary-700 dark:text-primary-400">
                          {(m.nomeCompleto || '?')[0].toUpperCase()}
                        </span>
                      </div>

                      {/* Info minore */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {m.nomeCompleto}
                        </p>
                        <p className="text-xs text-gray-500">
                          {age !== null && `${age} anni`}
                          {m.dataIngresso && <>{age !== null && ' · '}Ingresso: {new Date(m.dataIngresso).toLocaleDateString('it-IT', { month: 'short', year: 'numeric' })}</>}
                        </p>
                      </div>

                      {/* Badge stato / expand */}
                      {hasPep ? (
                        <span className="flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
                          <CheckCircle className="h-3.5 w-3.5" />
                          PEP attivo
                        </span>
                      ) : isSelected ? (
                        <button
                          onClick={() => toggleExpanded(m._id)}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                        >
                          {config?.expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                      ) : null}
                    </div>

                    {/* Form inline (solo se selezionato ed espanso) */}
                    {isSelected && config?.expanded && (
                      <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-gray-700 space-y-3">
                        {/* Ente inviante + Data avvio */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[11px] font-medium text-gray-500 mb-1">Ente inviante</label>
                            <select
                              value={config.enteInviante}
                              onChange={(e) => updateConfig(m._id, 'enteInviante', e.target.value)}
                              className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2.5 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                              <option value="">-- Opzionale --</option>
                              {ENTE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-[11px] font-medium text-gray-500 mb-1">Data avvio *</label>
                            <input
                              type="date"
                              value={config.dataAvvio}
                              onChange={(e) => updateConfig(m._id, 'dataAvvio', e.target.value)}
                              className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2.5 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          </div>
                        </div>

                        {/* Obiettivi */}
                        <div>
                          <label className="block text-[11px] font-medium text-gray-500 mb-1">Obiettivi</label>
                          <div className="space-y-2">
                            {config.obiettivi.map((ob, obIdx) => (
                              <div key={obIdx} className="flex items-start gap-2">
                                {/* Area */}
                                <select
                                  value={ob.area}
                                  onChange={(e) => updateObiettivo(m._id, obIdx, 'area', e.target.value)}
                                  className="text-xs border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-32 flex-shrink-0"
                                >
                                  {AREA_OPTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                                </select>

                                {/* Titolo */}
                                <input
                                  type="text"
                                  value={ob.titolo}
                                  onChange={(e) => updateObiettivo(m._id, obIdx, 'titolo', e.target.value)}
                                  placeholder="Titolo obiettivo *"
                                  maxLength={200}
                                  className="flex-1 text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2.5 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />

                                {/* Priorità */}
                                <select
                                  value={ob.priorita}
                                  onChange={(e) => updateObiettivo(m._id, obIdx, 'priorita', e.target.value)}
                                  className="text-xs border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-20 flex-shrink-0"
                                >
                                  {PRIORITA_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                </select>

                                {/* Rimuovi */}
                                {config.obiettivi.length > 1 && (
                                  <button
                                    onClick={() => removeObiettivo(m._id, obIdx)}
                                    className="text-gray-400 hover:text-red-500 p-1 flex-shrink-0"
                                    title="Rimuovi obiettivo"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Aggiungi obiettivo */}
                          <button
                            onClick={() => addObiettivo(m._id)}
                            className="mt-2 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 flex items-center gap-1"
                          >
                            <Plus className="h-3 w-3" />
                            Altro obiettivo
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && minori.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            {/* Conteggi */}
            <p className="text-xs text-gray-500">
              <span className="font-semibold text-gray-700 dark:text-gray-300">{selectedCount}</span> da creare
              {pepEsistenti > 0 && <> · <span className="text-green-600">{pepEsistenti}</span> già esistenti</>}
              {saltati > 0 && <> · {saltati} saltati</>}
            </p>

            {/* Azioni */}
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                disabled={submitting}
                className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 px-3 py-2"
              >
                Annulla
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !isValid()}
                className="flex items-center gap-2 text-sm font-medium bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                Crea {selectedCount} PEP
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function calcAge(d) {
  const t = new Date(), b = new Date(d)
  let a = t.getFullYear() - b.getFullYear()
  if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a--
  return a
}
